/**
 * BTC Deposit Modal - Squid Router Integration
 * Allows users to deposit native BTC which gets bridged to cbBTC/WBTC and deposited into vault
 */

import { useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useAccount } from 'wagmi'
import toast from 'react-hot-toast'
import { QRCodeSVG } from 'qrcode.react'
import {
  initiateBTCDeposit,
  getSquidStatus,
  saveBTCDepositToHistory,
  updateBTCDepositStatus,
  BTCDepositHistory,
} from '../../services/squidRouter'
import { Button } from '../common/Button'
import { Input as _Input } from '../common/Input'
import { useTokenPrice } from '../../hooks/useTokenPrice'
import { SUPPORTED_TOKENS } from '@contracts/tokens'

interface BTCDepositModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const MINIMUM_BTC = 0.0007 // Squid Router minimum (70,000 satoshis)
const BTC_PRICE_FALLBACK_USD = 93500 // Used only if live price fetch fails

// Preselected USD amounts
const PRESET_USD_AMOUNTS = [
  { usd: 1000, label: '$1,000' },
  { usd: 25000, label: '$25,000' },
  { usd: 500000, label: '$500,000' },
]

// Helper to format satoshis display
const formatSatoshis = (btcAmount: string) => {
  const satoshis = Math.floor(parseFloat(btcAmount) * 100000000)
  return satoshis.toLocaleString()
}

// Helper to convert USD to BTC using a live price (with fallback)
const usdToBtc = (usdAmount: number, btcPriceUsd: number): string => {
  const price = btcPriceUsd > 0 ? btcPriceUsd : BTC_PRICE_FALLBACK_USD
  return (usdAmount / price).toFixed(8)
}

// Helper to validate Bitcoin address format
const isValidBitcoinAddress = (address: string): boolean => {
  // Legacy P2PKH addresses (1...)
  const legacyRegex = /^1[a-km-zA-HJ-NP-Z1-9]{25,34}$/
  // Legacy P2SH addresses (3...)
  const p2shRegex = /^3[a-km-zA-HJ-NP-Z1-9]{25,34}$/
  // Bech32 SegWit addresses (bc1...)
  const bech32Regex = /^bc1[a-z0-9]{39,87}$/
  
  return legacyRegex.test(address) || p2shRegex.test(address) || bech32Regex.test(address)
}

export function BTCDepositModal({ isOpen, onClose, onSuccess }: BTCDepositModalProps) {
  const { address } = useAccount()

  // Live BTC price (fallback used if fetch fails)
  const { data: livePrice } = useTokenPrice(SUPPORTED_TOKENS.cbBTC)
  const btcPriceUsd = livePrice && livePrice > 0 ? livePrice : BTC_PRICE_FALLBACK_USD

  // Form state
  const [btcAmount, setBtcAmount] = useState('')
  const [bitcoinAddress, setBitcoinAddress] = useState('') // User's Bitcoin wallet address
  const slippage = 1 // Fixed at 1% - no user input needed
  
  // Flow state
  const [step, setStep] = useState<'input' | 'loading' | 'deposit' | 'monitoring'>('input')
  const [isLoading, setIsLoading] = useState(false)
  
  // Deposit info
  const [depositAddress, setDepositAddress] = useState('')
  const [depositAmount, setDepositAmount] = useState('')
  const [targetToken, setTargetToken] = useState<'cbBTC' | 'WBTC'>('cbBTC')
  const [estimatedOutput, setEstimatedOutput] = useState('')
  const [exchangeRate, setExchangeRate] = useState('')
  const [trackingId, setTrackingId] = useState('')
  const [_estimatedDuration, setEstimatedDuration] = useState(0)
  
  // Current deposit ID for history tracking
  const [currentDepositId, setCurrentDepositId] = useState('')
  
  // Polling state
  const [pollingTimeRemaining, setPollingTimeRemaining] = useState(0)
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null)
  const [countdownInterval, setCountdownInterval] = useState<NodeJS.Timeout | null>(null)

  // Calculate USD value using live price
  const usdValue = btcAmount ? (parseFloat(btcAmount) * btcPriceUsd).toFixed(2) : '0.00'

  // Reset form
  const handleClose = () => {
    if (step !== 'monitoring') {
      setBtcAmount('')
      setBitcoinAddress('')
      setStep('input')
      setDepositAddress('')
      setTrackingId('')
    }
    onClose()
  }

  // Generate deposit address
  const handleGenerateAddress = async () => {
    if (!address) {
      toast.error('Please connect your wallet')
      return
    }

    if (!bitcoinAddress) {
      toast.error('Please enter your Bitcoin address')
      return
    }

    if (!isValidBitcoinAddress(bitcoinAddress)) {
      toast.error('Please enter a valid Bitcoin address (legacy, P2SH, or bc1)')
      return
    }

    const amount = parseFloat(btcAmount)
    if (!btcAmount || amount <= 0) {
      toast.error('Please enter a valid BTC amount')
      return
    }

    if (amount < MINIMUM_BTC) {
      toast.error(`Minimum deposit is ${MINIMUM_BTC} BTC`)
      return
    }

    setIsLoading(true)
    setStep('loading')

    try {
      const satoshis = Math.floor(parseFloat(btcAmount) * 100000000)
      console.log('🚀 [BTC Deposit] Initiating deposit:', {
        btcAmount,
        satoshis,
        bitcoinAddress,
        toAddress: address,
        slippage,
      })

      // Get best route and deposit address
      // fromAddress = user's Bitcoin wallet (where they're sending from)
      // toAddress = user's Ethereum wallet (where they receive wrapped BTC)
      const result = await initiateBTCDeposit(
        btcAmount,
        address,
        slippage,
        bitcoinAddress // Pass Bitcoin address as fromAddress
      )

      console.log('✅ [BTC Deposit] Deposit address generated:', result)

      // Extract data from route and depositInfo
      // depositInfo only has: depositAddress, amount (satoshis), chainflipStatusTrackingId
      // route has: estimate.toAmount, estimate.exchangeRate, estimate.estimatedRouteDuration
      const routeEstimate = result.route.route.estimate

      setDepositAddress(result.depositInfo.depositAddress)
      setDepositAmount(btcAmount) // Store as BTC, not satoshis
      setTargetToken(result.token)
      setEstimatedOutput(routeEstimate.toAmount) // From route estimate
      setExchangeRate(routeEstimate.exchangeRate) // From route estimate
      setTrackingId(result.depositInfo.chainflipStatusTrackingId)
      setEstimatedDuration(routeEstimate.estimatedRouteDuration) // From route estimate (in seconds)

      // Create deposit history entry
      const depositId = `btc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      setCurrentDepositId(depositId)

      const historyEntry: BTCDepositHistory = {
        id: depositId,
        createdAt: Date.now(),
        btcAmount,
        depositAddress: result.depositInfo.depositAddress,
        targetToken: result.token,
        estimatedOutput: routeEstimate.toAmount,
        exchangeRate: routeEstimate.exchangeRate,
        toAddress: address,
        chainflipStatusTrackingId: result.depositInfo.chainflipStatusTrackingId,
        status: 'pending',
      }

      saveBTCDepositToHistory(historyEntry)

      setStep('deposit')
      toast.success(`Deposit address generated! Send BTC to receive ${result.token}`)
    } catch (error) {
      console.error('❌ [BTC Deposit] Error generating address:', error)
      
      // Enhanced error logging for debugging
      if (error instanceof Error) {
        console.error('❌ [BTC Deposit] Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
        })
        
        // Show more specific error message if available
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          toast.error('API authentication failed. Check integrator ID.')
        } else if (error.message.includes('400') || error.message.includes('SCHEMA_VALIDATION_ERROR')) {
          toast.error('Invalid request. Check console for details.')
        } else if (error.message.includes('timeout') || error.message.includes('network')) {
          toast.error('Network error. Please check your connection.')
        } else {
          toast.error(`Failed to generate address: ${error.message}`)
        }
      } else {
        toast.error('Failed to generate deposit address. Please try again.')
      }
      
      setStep('input')
    } finally {
      setIsLoading(false)
    }
  }

  // Copy address to clipboard
  const handleCopyAddress = () => {
    navigator.clipboard.writeText(depositAddress)
    toast.success('Deposit address copied!')
  }

  // Start monitoring (user confirms they sent BTC)
  const handleConfirmSent = () => {
    setStep('monitoring')
    updateBTCDepositStatus(currentDepositId, {
      status: 'deposited',
    })
    toast.success('Monitoring your deposit... (5 minute window)')
    
    // Start polling status
    startStatusPolling()
  }

  // Poll transaction status with 5-minute window
  const startStatusPolling = () => {
    if (!trackingId) return

    // Clean up any existing intervals
    if (pollingInterval) clearInterval(pollingInterval)
    if (countdownInterval) clearInterval(countdownInterval)

    // Set 5-minute timeout (300 seconds)
    const POLLING_DURATION = 300 // 5 minutes in seconds
    const POLL_INTERVAL = 10000 // Poll every 10 seconds
    const startTime = Date.now()
    const endTime = startTime + (POLLING_DURATION * 1000)
    
    setPollingTimeRemaining(POLLING_DURATION)

    // Countdown timer (updates every second)
    const countdown = setInterval(() => {
      const now = Date.now()
      const remaining = Math.max(0, Math.floor((endTime - now) / 1000))
      setPollingTimeRemaining(remaining)

      if (remaining === 0) {
        clearInterval(countdown)
      }
    }, 1000)
    setCountdownInterval(countdown)

    // Status polling (every 10 seconds)
    let failureCount = 0
    const MAX_FAILURES = 3 // Stop after 3 consecutive 400 errors
    
    const poll = setInterval(async () => {
      const now = Date.now()
      const timeElapsed = now - startTime

      // Stop polling after 5 minutes
      if (timeElapsed >= POLLING_DURATION * 1000) {
        console.log('⏰ [BTC Deposit] 5-minute polling window expired')
        clearInterval(poll)
        clearInterval(countdown)
        setPollingInterval(null)
        setCountdownInterval(null)
        
        toast('Polling stopped. Check history for updates.', {
          icon: '⏰',
          duration: 5000,
        })
        return
      }

      try {
        const status = await getSquidStatus({
          chainflipStatusTrackingId: trackingId,
          bridgeType: 'chainflipmultihop',
        })

        // Reset failure count on successful response
        failureCount = 0

        console.log('📊 [BTC Deposit] Status update:', {
          status: status.squidTransactionStatus,
          timeRemaining: Math.floor((endTime - Date.now()) / 1000) + 's',
        })

        if (status.squidTransactionStatus === 'success') {
          clearInterval(poll)
          clearInterval(countdown)
          setPollingInterval(null)
          setCountdownInterval(null)
          
          updateBTCDepositStatus(currentDepositId, {
            status: 'completed',
            txHash: status.toChain?.transactionId,
            completedAt: Date.now(),
          })
          
          toast.success('✅ Deposit completed successfully!')
          onSuccess?.()
          
          // Close modal after short delay
          setTimeout(() => handleClose(), 2000)
        } else if (status.squidTransactionStatus === 'failed') {
          clearInterval(poll)
          clearInterval(countdown)
          setPollingInterval(null)
          setCountdownInterval(null)
          
          updateBTCDepositStatus(currentDepositId, {
            status: 'failed',
            errorMessage: status.error?.message || 'Transaction failed',
            completedAt: Date.now(),
          })
          
          toast.error('❌ Deposit failed. Check history for details.')
        } else if (status.squidTransactionStatus === 'ongoing') {
          // Update to bridging status
          updateBTCDepositStatus(currentDepositId, {
            status: 'bridging',
          })
        }
      } catch (error: any) {
        // Check if this is a 400 error (invalid tracking ID)
        if (error?.status === 400) {
          failureCount++
          console.warn(`⚠️ [BTC Deposit] Invalid tracking ID (${failureCount}/${MAX_FAILURES})`)
          
          if (failureCount >= MAX_FAILURES) {
            // Stop polling after repeated failures
            clearInterval(poll)
            clearInterval(countdown)
            setPollingInterval(null)
            setCountdownInterval(null)
            
            updateBTCDepositStatus(currentDepositId, {
              status: 'failed',
              errorMessage: 'Tracking ID not found. No BTC was sent to the deposit address.',
              completedAt: Date.now(),
            })
            
            console.warn('⚠️ [BTC Deposit] Stopped polling - tracking ID invalid (no BTC sent)')
          }
        } else {
          // For other errors, just log and continue
          console.warn('⚠️ [BTC Deposit] Status check failed:', error?.message || error)
        }
      }
    }, POLL_INTERVAL)

    setPollingInterval(poll)
  }

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) clearInterval(pollingInterval)
      if (countdownInterval) clearInterval(countdownInterval)
    }
  }, [pollingInterval, countdownInterval])

  // Format BTC amount
  const formatBTC = (amount: string) => {
    const num = parseFloat(amount)
    if (isNaN(num)) return '0'
    return num.toFixed(8)
  }

  return (
    <Transition show={isOpen} as="div">
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-xl transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-xl font-bold text-white flex items-center gap-2">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.546zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.975.225.955.236c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.524 2.75 2.084z"/>
                      </svg>
                      Deposit Native BTC
                    </Dialog.Title>
                    <button
                      onClick={handleClose}
                      className="text-white/80 hover:text-white transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-sm text-white/90 mt-1">
                    Bridge BTC to {targetToken || 'wrapped BTC'} on Ethereum (best rate)
                  </p>
                </div>

                {/* Content */}
                <div className="px-6 py-6">
                  {/* Step 1: Input Amount */}
                  {step === 'input' && (
                    <div className="space-y-4">
                      {/* Bitcoin Address Input */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Your Bitcoin Address
                        </label>
                        <input
                          type="text"
                          placeholder="bc1... or 1... or 3..."
                          value={bitcoinAddress}
                          onChange={(e) => setBitcoinAddress(e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-mono text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Supports Legacy (1...), P2SH (3...), and SegWit (bc1...) addresses
                        </p>
                      </div>

                      {/* BTC Amount Input */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          BTC Amount
                        </label>
                        
                        {/* Preset Amount Buttons */}
                        <div className="flex gap-2 mb-3">
                          {PRESET_USD_AMOUNTS.map((preset) => (
                            <button
                              key={preset.usd}
                              onClick={() => setBtcAmount(usdToBtc(preset.usd, btcPriceUsd))}
                              className="flex-1 px-3 py-2 text-sm font-semibold bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all shadow-sm hover:shadow-md"
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>

                        <input
                          type="number"
                          placeholder="0.001"
                          step="0.0001"
                          min={MINIMUM_BTC}
                          value={btcAmount}
                          onChange={(e) => setBtcAmount(e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange text-lg font-semibold"
                        />
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500 dark:text-gray-400">
                              Minimum: {MINIMUM_BTC} BTC
                            </span>
                            {btcAmount && parseFloat(btcAmount) > 0 && (
                              <span className="text-gray-700 dark:text-gray-300 font-semibold">
                                ≈ ${usdValue} USD
                              </span>
                            )}
                          </div>
                          {btcAmount && parseFloat(btcAmount) > 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formatSatoshis(btcAmount)} satoshis
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>How it works:</strong>
                          <br />
                          1. We'll compare rates for cbBTC and WBTC on Ethereum
                          <br />
                          2. Generate a BTC deposit address (best rate)
                          <br />
                          3. Send BTC from your wallet to the generated address
                          <br />
                          4. Receive wrapped BTC in your Ethereum vault automatically
                          <br />
                          <br />
                          <strong className="text-brand-orange">⏱️ Processing Time:</strong> This process will take 10-20 minutes after you send the BTC. Track progress in your deposit history below.
                        </p>
                      </div>

                      <Button
                        onClick={handleGenerateAddress}
                        disabled={isLoading || !bitcoinAddress || !btcAmount || parseFloat(btcAmount) < MINIMUM_BTC}
                        className="w-full py-3 text-base font-semibold"
                      >
                        {isLoading ? 'Generating Address...' : 'Generate Deposit Address'}
                      </Button>
                    </div>
                  )}

                  {/* Step 2: Loading */}
                  {step === 'loading' && (
                    <div className="text-center py-12">
                      <svg
                        className="animate-spin h-16 w-16 mx-auto text-brand-orange"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <p className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                        Generating Deposit Address...
                      </p>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Comparing rates and finding the best option for you
                      </p>
                    </div>
                  )}

                  {/* Step 3: Show Deposit Address */}
                  {step === 'deposit' && (
                    <div className="space-y-4">
                      <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-300 dark:border-green-700">
                        <div className="flex items-start gap-3">
                          <svg className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="font-semibold text-green-900 dark:text-green-100">
                              Best Rate: {targetToken}
                            </p>
                            <p className="text-sm text-green-800 dark:text-green-200 mt-1">
                              You'll receive ~{estimatedOutput ? (parseFloat(estimatedOutput) / 100000000).toFixed(8) : '0'} {targetToken}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* QR Code Section */}
                      <div className="flex justify-center py-4">
                        <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-300 dark:border-gray-600">
                          <QRCodeSVG
                            value={`bitcoin:${depositAddress}?amount=${depositAmount}`}
                            size={200}
                            level="M"
                            includeMargin={true}
                          />
                          <p className="text-xs text-center text-gray-600 dark:text-gray-400 mt-2">
                            Scan with Bitcoin wallet
                          </p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Send Exactly This Amount
                        </label>
                        <div className="flex gap-2">
                          <div className="flex-1 px-4 py-3 rounded-lg border-2 border-brand-orange bg-orange-50 dark:bg-orange-900/20 font-mono text-lg font-bold text-gray-900 dark:text-white">
                            {formatBTC(depositAmount)} BTC
                          </div>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(formatBTC(depositAmount))
                              toast.success('Amount copied!')
                            }}
                            className="px-4 py-2 rounded-lg bg-gray-700 dark:bg-gray-600 hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors text-white"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          To This Address
                        </label>
                        <div className="flex gap-2">
                          <div className="flex-1 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-mono text-sm break-all text-gray-900 dark:text-white">
                            {depositAddress}
                          </div>
                          <button
                            onClick={handleCopyAddress}
                            className="px-4 py-2 rounded-lg bg-gray-700 dark:bg-gray-600 hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors text-white flex-shrink-0"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-300 dark:border-yellow-700">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div>
                              <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-100">
                                Important: Send the exact amount
                              </p>
                              <p className="text-xs text-yellow-800 dark:text-yellow-200 mt-1">
                                The exchange rate may change if you wait too long. Send BTC as soon as possible.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                Processing Time: 10-20 minutes
                              </p>
                              <p className="text-xs text-blue-800 dark:text-blue-200 mt-1">
                                After sending BTC, the cross-chain bridge will take 10-20 minutes to process your deposit. You can monitor progress in real-time or check back later.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">Exchange Rate:</p>
                          <p className="font-semibold text-gray-900 dark:text-white">{exchangeRate}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">Est. Duration:</p>
                          <p className="font-semibold text-gray-900 dark:text-white">~11 min</p>
                        </div>
                      </div>

                      <Button
                        onClick={handleConfirmSent}
                        className="w-full py-3 text-base font-semibold"
                      >
                        I've Sent the BTC
                      </Button>

                      <button
                        onClick={() => setStep('input')}
                        className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                      >
                        Cancel and start over
                      </button>
                    </div>
                  )}

                  {/* Step 4: Monitoring */}
                  {step === 'monitoring' && (
                    <div className="text-center py-8">
                      <div className="relative inline-block">
                        <svg
                          className="animate-spin h-16 w-16 text-brand-orange"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      </div>
                      <p className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                        Monitoring Your Deposit
                      </p>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Waiting for BTC transaction confirmation...
                      </p>
                      
                      {/* Polling countdown */}
                      {pollingTimeRemaining > 0 && (
                        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-full">
                          <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                            Active polling: {Math.floor(pollingTimeRemaining / 60)}:{String(pollingTimeRemaining % 60).padStart(2, '0')}
                          </span>
                        </div>
                      )}
                      
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                        Bitcoin transactions usually take 10-20 minutes to confirm
                      </p>

                      <div className="mt-6 space-y-3">
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-left">
                              <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                Status Polling Active (5 minutes)
                              </p>
                              <p className="text-xs text-blue-800 dark:text-blue-200 mt-1">
                                We're checking for your deposit every 10 seconds. Even after polling stops, your deposit will continue processing in the background.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                            You can safely close this window. We'll track the progress in your deposit history below.
                          </p>
                          <button
                            onClick={handleClose}
                            className="text-sm text-brand-orange hover:text-brand-orange/80 font-semibold"
                          >
                            Close and view history →
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

